import type { ApplicantCreation } from 'cpool-schemas';
import type { Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SubTitle } from './shared/SubTitle';
import { TextInput } from 'cpool-components/input/TextInput';

import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { useCountryOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';

const basePath = 'beschaeftigung.rentner' as const;

export const Pensioner: React.FC = () => {
    const { t } = useTranslation();
    const { control, register } = useFormContext<ApplicantCreation>();
    const countryOptions = useCountryOptions();

    return (
        <div className="mt-4 space-y-2">
            <FormDateInput
                id={`${basePath}.rentnerSeit` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.PENSIONER_SINCE')}
                control={control}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.INCOME_HEALTH_INSURANCE')}</SubTitle>

            <FormCurrencyInput
                id={`${basePath}.staatlicheRenteMonatlich` as Path<ApplicantCreation>}
                label={t('CREDIT_WORTHINESS.SECTIONS.INCOME.AMOUNT_OF_PENSION')}
                control={control}
                min={0}
            />

            <SubTitle>{t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.SUBSECTIONS.PENSION_FUND')}</SubTitle>

            <TextInput
                id={`${basePath}.rentenversicherung.name`}
                label={t('CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT.PENSION_FUND_NAME')}
                labelClassName="w-[175px] justify-start"
                {...register(`${basePath}.rentenversicherung.name` as Path<ApplicantCreation>)}
            />

            {/* Straße, Hausnummer */}
            <div className="flex gap-2.5 w-full justify-between items-start">
                <label className="min-w-[175px] justify-start text-sm text-midnight text-left mt-1.5">
                    {t('ADDRESS.STREET')}, {t('ADDRESS.HOUSE_NUMBER')}
                </label>
                <div className="flex flex-col w-3/4">
                    <TextInput
                        id={`${basePath}.rentenversicherung.anschrift.strasse`}
                        {...register(`${basePath}.rentenversicherung.anschrift.strasse` as Path<ApplicantCreation>)}
                    />
                </div>
                <div className="flex flex-col w-1/4">
                    <TextInput
                        id={`${basePath}.rentenversicherung.anschrift.hausnummer`}
                        {...register(`${basePath}.rentenversicherung.anschrift.hausnummer` as Path<ApplicantCreation>)}
                    />
                </div>
            </div>

            {/* PLZ, Ort */}
            <div className="flex gap-2.5 w-full justify-between items-start">
                <label className="min-w-[175px] justify-start text-sm text-midnight text-left mt-1.5">
                    {t('ADDRESS.ZIP_CODE_SHORT')}, {t('ADDRESS.LOCATION')}
                </label>
                <div className="flex flex-col w-1/4">
                    <TextInput
                        id={`${basePath}.rentenversicherung.anschrift.plz`}
                        {...register(`${basePath}.rentenversicherung.anschrift.plz` as Path<ApplicantCreation>)}
                    />
                </div>
                <div className="flex flex-col w-3/4">
                    <TextInput
                        id={`${basePath}.rentenversicherung.anschrift.ort`}
                        {...register(`${basePath}.rentenversicherung.anschrift.ort` as Path<ApplicantCreation>)}
                    />
                </div>
            </div>

            <FormDropdown
                id={`${basePath}.rentenversicherung.anschrift.land` as Path<ApplicantCreation>}
                label={t('ADDRESS.COUNTRY')}
                options={countryOptions}
                control={control}
            />
        </div>
    );
};
